import { useCallback, useState } from 'react';

import * as actions from '../../containers/SilverfortNodes/silverfortNodesActions';
import { NODE_FIELDS, PROTECTED_DOMAIN_FIELDS } from '../../common/fields';
import Account from '../Controls/Account/Account';
import SelectField from '../Controls/SelectField/SelectField';
import SwitchField from '../Controls/SwitchField/SwitchField';
import { TEST_ACCOUNTS } from '@silverfort-nodelibs/common/utils/consts';
import AccountPopup from '../Controls/Account/AccountPopup';

import styles from './NodeDomain.module.css';

const NodeDomain = ({
    fields,
    dispatch,
    index,
    nodeId,
    domainsOptions,
    domainsIdInUse,
    ldapServerOptions,
    isRadiusSet,
    multiDomainEnabled
}) => {
    const [showAccountPopup, setShowAccountPopup] = useState(false);
    const domain = fields[NODE_FIELDS.DOMAIN.ID];
    const ldapUrl = fields[NODE_FIELDS.LDAP_URL.ID] || {};
    const useSecureLdap = fields[NODE_FIELDS.USE_SECURE_LDAP.ID] || {};
    let adsyncNode = fields[NODE_FIELDS.ADSYNC_NODE.ID] || {};
    let isRadiusProtected = fields[NODE_FIELDS.RADIUS_NODE.ID] || {};
    const dcIps = fields[NODE_FIELDS.DC_ADDRESS.ID];
    const radiusAccount = fields[NODE_FIELDS.RADIUS_MACHINE_ACCOUNT.ID];
    const availableDomains = { ...domainsOptions };
    for (const domainId of domainsIdInUse) {
        if (domainId !== domain.value) {
            delete availableDomains[domainId];
        }
    }

    if (multiDomainEnabled) {
        adsyncNode = {
            ...adsyncNode,
            desc: 'Each Protected Domain must have at least one Node enabled to perform AD Sync. For optimal performance, specify two Nodes per Protected Domain and choose the Nodes with the best connectivity to the LDAP server.'
        };
        isRadiusProtected = { ...isRadiusProtected, desc: 'Only one RADIUS server can be configured per node.' };
    }
    const testMetadata = {
        nodeId,
        domainId: domain ? domain.value : '',
        dcIps: dcIps ? dcIps.value : [],
        useSecureLdap: useSecureLdap ? useSecureLdap.value : '',
        domainData: domain && domainsOptions[domain.value] ? domainsOptions[domain.value].domainData : ''
    };

    const dispatchUpdate = useCallback(
        (fieldId, value) => {
            if (
                (fieldId === NODE_FIELDS.DOMAIN.ID && ldapUrl.value) ||
                (fieldId === NODE_FIELDS.LDAP_URL.ID && domain.value)
            ) {
                const domainOption =
                    fieldId === NODE_FIELDS.DOMAIN.ID ? domainsOptions[value] : domainsOptions[domain.value];
                if (domainOption && !domainOption.serviceAccountUser) {
                    setShowAccountPopup(true);
                }
            }
            if (
                fieldId === NODE_FIELDS.DOMAIN.ID &&
                domainsOptions[value] &&
                !domainsOptions[value].serviceAccountUser
            ) {
                dispatch({
                    type: actions.UPDATE_NODE,
                    payload: {
                        nodeId,
                        fieldId: NODE_FIELDS.DOMAINS.ID,
                        index: index,
                        arrFieldId: PROTECTED_DOMAIN_FIELDS.DOMAIN_SERVICE_ACCOUNT.ID,
                        value: { user: '' }
                    }
                });
            }

            dispatch({
                type: actions.UPDATE_NODE,
                payload: { nodeId, fieldId: NODE_FIELDS.DOMAINS.ID, index: index, arrFieldId: fieldId, value }
            });
        },
        [dispatch, index, nodeId, domainsOptions, domain.value, ldapUrl.value]
    );

    const ldapIpChanged = (fieldId, value) => {
        let existingOption = ldapServerOptions.find(
            (option) => option.value.toLowerCase() === value.value.toLowerCase().trim()
        );
        if (!existingOption) {
            value = {
                ...value,
                label: value.label?.trim() || value.label,
                value: value.value?.trim() || value.value
            };
            dispatchLdapIpAdded(fieldId, value);
            existingOption = value;
        }
        dispatchUpdate(fieldId, existingOption.value);
    };

    const dcAddressChanged = (fieldId, value) => {
        for (const valueEntry of value) {
            let existingOption = ldapServerOptions.find(
                (option) => option.value.toLowerCase() === valueEntry.value.toLowerCase()
            );
            if (!existingOption) {
                dispatchLdapIpAdded(fieldId, valueEntry);
                existingOption = value;
            }
        }

        dispatchUpdate(
            fieldId,
            value.map((valueEntry) => valueEntry.value)
        );
    };

    const dispatchLdapIpAdded = useCallback(
        (fieldId, value) => {
            dispatch({
                type: actions.LDAP_IP_ADDED,
                payload: { fieldId, value }
            });
        },
        [dispatch]
    );

    const toggleDomainAccountPopup = useCallback(() => {
        setShowAccountPopup((s) => !s);
    }, []);

    const validateDomainName = useCallback((accountDomainName) => {
        if (!accountDomainName) {
            return 'Service account must have domain name';
        }
    }, []);

    const onSaveDomainServiceAccount = useCallback(
        (user, pass) => {
            setShowAccountPopup(false);
            dispatchUpdate(PROTECTED_DOMAIN_FIELDS.DOMAIN_SERVICE_ACCOUNT.ID, { user, pass });
        },
        [dispatchUpdate]
    );

    return (
        <div className={styles.wrapper}>
            {domain && (
                <SelectField
                    field={domain}
                    changed={dispatchUpdate}
                    options={Object.values(availableDomains)}
                    clearable={true}
                />
            )}
            {adsyncNode.id && <SwitchField field={adsyncNode} changed={dispatchUpdate} marginTop="array" />}
            {ldapUrl.id && (
                <SelectField
                    field={ldapUrl}
                    changed={ldapIpChanged}
                    options={ldapServerOptions}
                    creatable={true}
                    expendDescription={true}
                />
            )}
            {useSecureLdap.id && <SwitchField field={useSecureLdap} changed={dispatchUpdate} marginTop="array" />}
            {isRadiusProtected.id && (
                <SwitchField
                    field={isRadiusProtected}
                    changed={dispatchUpdate}
                    disabled={isRadiusSet && !isRadiusProtected.value}
                />
            )}
            <br />
            {dcIps && isRadiusProtected.value && (
                <SelectField
                    multichoice={true}
                    field={dcIps}
                    changed={dcAddressChanged}
                    options={ldapServerOptions}
                    creatable={true}
                />
            )}
            {radiusAccount && isRadiusProtected.value && (
                <Account
                    field={radiusAccount}
                    changed={dispatchUpdate}
                    testMetadata={{ ...testMetadata, accountType: TEST_ACCOUNTS.RADIUS_ACCOUNT }}
                    marginTop={false}
                />
            )}
            {showAccountPopup && (
                <AccountPopup
                    userValue={domainsOptions[domain.value].serviceAccountUser}
                    validateDomainName={validateDomainName}
                    testMetadata={{
                        nodeId: nodeId,
                        domainId: domain.value,
                        accountType: TEST_ACCOUNTS.SERVICE_ACCOUNT,
                        ldapURL: ldapUrl && ldapUrl.value ? ldapUrl.value : null,
                        shouldSecureNodeLDAP: useSecureLdap ? useSecureLdap.value : null,
                        domainData: domainsOptions[domain.value].domainData
                    }}
                    onSave={onSaveDomainServiceAccount}
                    onCancel={toggleDomainAccountPopup}
                    header={`Enter the service account details in UPN format for domain '${
                        domainsOptions[domain.value].label
                    }'`}
                />
            )}
        </div>
    );
};

export default NodeDomain;
